<template>
  <div class="p-m-3">
    <h2>Duplicate Enquiries</h2>

    <!-- Permissions arrive asynchronously (App.vue fetches them after login), so an
         empty list means "not loaded yet", not "no access". -->
    <div v-if="permissionsPending">
      <ProgressSpinner style="width: 30px; height: 30px;" strokeWidth="6" />
    </div>
    <div v-else-if="permissionsFailed" class="perms-failed" style="color: #c62828;">
      Your permissions could not be loaded. Please reload the page.
    </div>
    <div v-else-if="!canView">
      <h3>Not authorised</h3>
    </div>
    <div v-else>
      <!-- Tabs. Hand-rolled with PrimeVue's own p-tabview classes: TabView in this
           version keeps every panel in the DOM (v-show) and has no activeIndex prop,
           which would mean three grids mounted at once. -->
      <div class="p-tabview p-component">
        <ul class="p-tabview-nav" role="tablist">
          <li
            v-for="tab in tabs"
            :key="tab.list"
            role="presentation"
            :class="{ 'p-highlight': tab.list === activeList }"
          >
            <a class="p-tabview-nav-link" role="tab" @click="selectTab(tab.list)">
              <span class="p-tabview-title">{{ tab.label }} ({{ countFor(tab.list) }})</span>
            </a>
          </li>
        </ul>
      </div>

      <!-- Controls. Every one of these is a server-side filter, so changing any of
           them re-fetches the current list. Live only, the three dropdowns and the
           product-type checkbox also change what the other tabs hold, so they re-fetch
           the tab counts as well. -->
      <div class="p-card p-p-3 p-mt-3 p-mb-3">
        <div class="p-d-flex p-ai-center p-flex-wrap" style="gap: 22px;">
          <div class="p-d-flex p-ai-center" style="gap: 8px;">
            <InputSwitch id="deLiveOnly" v-model="liveOnly" />
            <label for="deLiveOnly" style="margin: 0;">Live only</label>
          </div>
          <div v-if="listHasConfidence" class="p-d-flex p-ai-center" style="gap: 8px;">
            <label for="deConfidence" style="margin: 0;">Confidence</label>
            <Dropdown
              id="deConfidence"
              v-model="confidence"
              :options="confidenceOptions"
              style="width: 9rem;"
            />
          </div>
          <!-- Tabs 1 and 2 only: on tab 3 the two enquiries are different projects by
               definition, so a different product type says nothing there. A blank
               product type on either side is not "different" - it is unknown - so such
               a pair is kept. -->
          <div
            v-if="listHasProductTypeCheckbox"
            class="p-d-flex p-ai-center"
            style="gap: 8px;"
            title="Hides a pair when both enquiries have a product type and they are not the same. A pair with a blank product type is kept."
          >
            <Checkbox id="deHideDiffProductType" v-model="hideDiffProductType" :binary="true" />
            <label for="deHideDiffProductType" style="margin: 0;">Hide different product types</label>
          </div>
          <span class="p-input-icon-left">
            <i class="pi pi-search" />
            <InputText
              v-model="search"
              placeholder="Search quote, customer, site, project..."
              style="width: 22rem; max-width: 100%;"
            />
          </span>
          <ProgressSpinner v-if="loading" style="width: 24px; height: 24px;" strokeWidth="7" />
          <span v-else-if="!errorMessage">{{ recCount }} row(s)</span>
        </div>
        <!-- The three multi-selects. Their values are the ones the analysis actually
             holds (either side of any pair), and a pair matches when EITHER of its two
             enquiries has a chosen value - which each dropdown says underneath it. -->
        <div class="p-d-flex p-flex-wrap p-mt-3" style="gap: 22px;">
          <div v-for="f in sideFilters" :key="f.model" class="de-filter">
            <label :for="f.id" class="de-filter-label">{{ f.label }}</label>
            <MultiSelect
              :id="f.id"
              v-model="$data[f.model]"
              :options="filterOptions[f.options]"
              :filter="true"
              :placeholder="f.placeholder"
              style="width: 17rem; max-width: 100%;"
            />
            <small class="de-filter-hint">Shows a pair when either enquiry has one of these.</small>
          </div>
        </div>
        <div v-if="errorMessage" class="p-mt-2" style="color: #c62828;">{{ errorMessage }}</div>
      </div>

      <!-- Grid. Sorting is client-side; the order the server sends is the initial order.
           autoLayout is what makes the columns readable: without it PrimeVue lays the
           table out table-layout: fixed, which ignores the per-column min-widths and
           squeezes 13 to 18 columns into the card, so the text spills over its
           neighbours. autoLayout switches the table to table-layout: auto and gives
           .p-datatable-wrapper overflow-x: auto, so the table takes the width its
           columns ask for and scrolls sideways inside the card, header and body
           together, leaving the paginator at the screen's width. -->
      <div class="p-card p-p-3 p-mb-3">
        <DataTable
          :key="'de-grid-' + activeList"
          :value="gridRows"
          :autoLayout="true"
          :sortField.sync="sortField"
          :sortOrder.sync="sortOrder"
          :paginator="true"
          :first.sync="first"
          :rows.sync="rowsPerPage"
          :rowsPerPageOptions="[25, 50, 100]"
          :loading="loading"
          class="p-datatable-sm p-datatable-striped p-datatable-gridlines de-grid"
          dataKey="id"
          :rowHover="true"
          selectionMode="single"
          :selection.sync="selected"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
          @row-click="onRowClick"
        >
          <template #empty>No duplicate enquiries match these filters.</template>
          <template #loading>Loading duplicate enquiries. Please wait.</template>
          <Column
            v-for="col in columns"
            :key="col.field"
            :field="col.field"
            :header="col.header"
            :sortable="true"
            :headerStyle="headerStyleFor(col)"
            :bodyStyle="bodyStyleFor(col)"
          >
            <template #body="s">
              <span v-if="col.kind === 'chip'" :class="chipClass(s.data.confidence)">
                {{ s.data.confidence || '—' }}
              </span>
              <span v-else :style="cellStyleFor(col)">{{ cellText(s.data, col) }}</span>
            </template>
          </Column>
        </DataTable>
      </div>

      <!-- The comparison. A popup rather than a panel below the grid, so the pair is
           read side by side instead of underneath a grid that has pushed it off the
           screen. The Dialog of this PrimeVue version (2.0.7) has no Escape handling
           of its own - its only key handler is the Tab focus trap - so the page keeps
           its own Escape listener. v-if, not only :visible, because the body reads
           `selected` directly and slot content is built by this component whether the
           Dialog renders it or not. -->
      <Dialog
        v-if="selected"
        :visible.sync="comparisonVisible"
        :aria-label="comparisonHeader"
        :modal="true"
        :closable="true"
        position="center"
        :style="{ width: '80vw', maxWidth: '1200px' }"
        :contentStyle="{ maxHeight: '75vh', overflow: 'auto' }"
      >
        <!-- The header is built here rather than left to the Dialog's `header` prop,
             because Back / "x of y" / Next have to sit in the same row as the close X.
             PrimeVue 2.0.7 lays .p-dialog-header out as flex with space-between and
             renders its icons after this slot, so one flex-filling wrapper puts the
             title on the left and the walk hard up against the X on the right. -->
        <template #header>
          <div class="de-dialog-header">
            <span class="p-dialog-title">{{ comparisonHeader }}</span>
            <span class="de-walk">
              <Button
                icon="pi pi-chevron-left"
                class="p-button-text p-button-rounded de-walk-button"
                title="Back (left arrow)"
                aria-label="Back"
                :disabled="!canWalkBack"
                @click="walkBack"
              />
              <span class="de-walk-position">{{ walkPosition }}</span>
              <Button
                icon="pi pi-chevron-right"
                class="p-button-text p-button-rounded de-walk-button"
                title="Next (right arrow)"
                aria-label="Next"
                :disabled="!canWalkNext"
                @click="walkNext"
              />
            </span>
          </div>
        </template>

        <h4 style="margin: 0 0 6px 0;">
          {{ selected.reason || '—' }}
          <span v-if="selected.confidence" :class="chipClass(selected.confidence)">
            {{ selected.confidence }}
          </span>
        </h4>

        <ul v-if="proofItems.length" style="margin: 0 0 8px 0;">
          <li v-for="(item, i) in proofItems" :key="i">{{ item }}</li>
        </ul>

        <p v-if="selected.whatToCheck" style="margin: 0 0 12px 0;">
          <strong>What to check</strong> — {{ selected.whatToCheck }}
        </p>

        <table class="de-compare">
          <thead>
            <tr>
              <th></th>
              <th>
                Offending enquiry
                <Button
                  :label="'Open ' + (selected.quoteNo || '—')"
                  class="p-button-text p-button-sm"
                  :disabled="!selected.oppId"
                  @click="openEnquiry(selected.oppId)"
                />
              </th>
              <th>
                Original enquiry
                <Button
                  :label="'Open ' + (selected.oQuoteNo || '—')"
                  class="p-button-text p-button-sm"
                  :disabled="!selected.oOppId"
                  @click="openEnquiry(selected.oOppId)"
                />
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="field in compareFields" :key="field.label">
              <th>{{ field.label }}</th>
              <td :class="{ 'de-differs': !field.same }">{{ field.left }}</td>
              <td :class="{ 'de-differs': !field.same }">
                {{ field.right }}
                <i v-if="field.same" class="pi pi-check de-same-tick" title="matches"></i>
              </td>
            </tr>
          </tbody>
        </table>
      </Dialog>
    </div>
  </div>
</template>

<script>
import DuplicateEnquiryService from '@/service/DuplicateEnquiryService'

const DASH = '—'
const SEARCH_DEBOUNCE_MS = 400

// High first, then Medium, then Low, then no confidence at all - the order the
// server sends and the order a client-side sort on this column must reproduce.
const CONFIDENCE_RANK = { High: 1, Medium: 2, Low: 3 }

// width is the column's min-width, and for a wrapping column also the max-width of
// the text inside it, so the column settles at that width instead of stretching to
// the longest customer name on the page. wrap: true is for free text that has to be
// allowed to run onto a second line; everything else is a short value (a quote
// number, an amount, a date, a count) that must not be broken mid-value.
const COL = {
  quoteNo: { header: 'Offending quote no', field: 'quoteNo', kind: 'text', width: '110px' },
  oQuoteNo: { header: 'Original quote no', field: 'oQuoteNo', kind: 'text', width: '110px' },
  custName: { header: 'Customer', field: 'custName', kind: 'text', width: '220px', wrap: true },
  oCustName: { header: 'Original customer', field: 'oCustName', kind: 'text', width: '220px', wrap: true },
  siteName: { header: 'Site', field: 'siteName', kind: 'text', width: '220px', wrap: true },
  town: { header: 'Town', field: 'town', kind: 'text', width: '130px', wrap: true },
  projectLabel: { header: 'Project', field: 'projectLabel', kind: 'text', width: '220px', wrap: true },
  oProjectLabel: { header: 'Original project', field: 'oProjectLabel', kind: 'text', width: '220px', wrap: true },
  reason: { header: 'Reason', field: 'reason', kind: 'text', width: '360px', wrap: true },
  confidence: { header: 'Confidence', field: 'confidenceRank', kind: 'chip', width: '110px' },
  productValue: { header: 'Offending quote £', field: 'productValue', kind: 'money', width: '120px' },
  oProductValue: { header: 'Original quote £', field: 'oProductValue', kind: 'money', width: '120px' },
  nCustomers: { header: 'Customers quoting', field: 'nCustomers', kind: 'int', width: '100px' },
  liveValueSum: { header: 'Project live quotes added up £', field: 'liveValueSum', kind: 'money', width: '150px' },
  liveValueAvg: { header: 'Project average £', field: 'liveValueAvg', kind: 'money', width: '130px' },
  liveOverCounted: { header: 'Over-counted £', field: 'liveOverCounted', kind: 'money', width: '120px' },
  status: { header: 'Status', field: 'status', kind: 'text', width: '100px' },
  created: { header: 'Created', field: 'createdSort', display: 'dateCreated', kind: 'date', width: '110px' },
  daysApart: { header: 'Days apart', field: 'daysApart', kind: 'int', width: '100px' }
}

const COLUMNS_BY_LIST = {
  1: [
    COL.quoteNo, COL.oQuoteNo, COL.custName, COL.siteName, COL.town, COL.projectLabel,
    COL.reason, COL.confidence, COL.productValue, COL.oProductValue, COL.status,
    COL.created, COL.daysApart
  ],
  2: [
    COL.quoteNo, COL.oQuoteNo, COL.custName, COL.oCustName, COL.siteName, COL.town,
    COL.projectLabel, COL.reason, COL.confidence, COL.productValue, COL.oProductValue,
    COL.nCustomers, COL.liveValueSum, COL.liveValueAvg, COL.liveOverCounted, COL.status,
    COL.created, COL.daysApart
  ],
  3: [
    COL.quoteNo, COL.oQuoteNo, COL.custName, COL.siteName, COL.town, COL.projectLabel,
    COL.oProjectLabel, COL.reason, COL.productValue, COL.oProductValue, COL.status,
    COL.created, COL.daysApart
  ]
}

// The numeric and date columns. PrimeVue 2.0.7 sorts with a comparator that always
// puts a null first ascending and last descending, and the DataTable of this version
// has no sortFunction hook to replace it, so a blank cell in one of these columns
// jumps to the top of an ascending sort. gridRows() below swaps those blanks for a
// sentinel chosen from the sort direction, which lands them at the bottom either way.
const NULLS_LAST_FIELDS = Object.keys(COL)
  .map(key => COL[key])
  .filter(col => col.kind === 'money' || col.kind === 'int' || col.kind === 'date')
  .map(col => col.field)

export default {
  name: 'DuplicateEnquiries',
  data() {
    return {
      duplicateEnquiryService: new DuplicateEnquiryService(),
      tabs: [
        { list: 1, label: 'Same customer, same project' },
        { list: 2, label: 'Same project, other customer' },
        { list: 3, label: 'Same site, other project' }
      ],
      activeList: 1,
      liveOnly: true,
      confidence: 'All',
      confidenceOptions: ['All', 'High', 'Medium', 'Low'],
      search: '',
      // The multi-select filters and the checkbox (Mohammed's UAT request, 2026-09-28).
      // An empty array is "no filter", never "match nothing".
      productTypes: [],
      branches: [],
      projTypes: [],
      hideDiffProductType: false,
      filterOptions: { productTypes: [], branches: [], projTypes: [] },
      sideFilters: [
        { id: 'deProductType', label: 'Product Type', model: 'productTypes', options: 'productTypes', placeholder: 'Any product type' },
        { id: 'deBranch', label: 'Branch', model: 'branches', options: 'branches', placeholder: 'Any branch' },
        { id: 'deProjType', label: 'Project Type', model: 'projTypes', options: 'projTypes', placeholder: 'Any project type' }
      ],
      rows: [],
      recCount: 0,
      counts: {},
      loading: false,
      errorMessage: null,
      selected: null,
      // Kept in step with the grid's own sort (.sync) because gridRows needs the
      // direction to decide which way blank cells have to be pushed.
      sortField: null,
      sortOrder: 1,
      // And in step with the grid's paginator (.sync), because walking past the end
      // of a page has to turn it. The page size is read back rather than fixed:
      // rowsPerPageOptions lets the user choose 25 or 100 instead.
      first: 0,
      rowsPerPage: 50,
      searchTimer: null,
      // Requests are fired on every filter change, so a slow earlier response must
      // not overwrite the rows of a later one - nor the counts of a later one.
      requestSeq: 0,
      countsSeq: 0
    }
  },
  computed: {
    myPermissions() {
      return this.$store.getters['auth/getUserPermissions'] || []
    },
    // App.vue records how the permissions fetch went. An empty list only means "still
    // loading" while that fetch has not finished; once it has, empty is "not authorised"
    // and a failed fetch is an error - never an endless spinner.
    permissionsStatus() {
      return this.$store.getters['auth/getUserPermissionsStatus']
    },
    permissionsPending() {
      return this.myPermissions.length === 0 && this.permissionsStatus !== 'loaded' && this.permissionsStatus !== 'error'
    },
    permissionsFailed() {
      return this.myPermissions.length === 0 && this.permissionsStatus === 'error'
    },
    canView() {
      const p = this.myPermissions.find(
        a => a.permission && a.permission.permissionCode === 'Report-DuplicateEnquiries'
      )
      return !!(p && p.permissionLevel !== 0)
    },
    columns() {
      return COLUMNS_BY_LIST[this.activeList] || []
    },
    // What the grid is actually given: the rows as loaded, except that when it is
    // sorted on a number or a date the blank cells of that one column carry a
    // sentinel so they sort to the bottom. Only blank cells are copied, so a row
    // with a value keeps its identity and the selected row stays selected.
    gridRows() {
      const field = this.sortField
      if (!field || NULLS_LAST_FIELDS.indexOf(field) === -1) {
        return this.rows
      }
      const sentinel = this.sortOrder < 0 ? -Infinity : Infinity
      return this.rows.map(row => {
        const value = row[field]
        if (value === null || value === undefined || value === '') {
          const copy = Object.assign({}, row)
          copy[field] = sentinel
          return copy
        }
        return row
      })
    },
    listHasConfidence() {
      // List 3 is reporting-only and carries no confidence at all.
      return this.activeList !== 3
    },
    // Tabs 1 and 2 are about one project quoted twice, where two different product
    // types can mean two different packages rather than a duplicate.
    listHasProductTypeCheckbox() {
      return this.activeList !== 3
    },
    // The dialog is open exactly while a pair is selected, and closing it - the X,
    // Escape, anything that clears the selection - is the one way it shuts.
    comparisonVisible: {
      get() {
        return !!this.selected
      },
      set(value) {
        if (!value) {
          this.selected = null
        }
      }
    },
    comparisonHeader() {
      if (!this.selected) {
        return ''
      }
      return this.dash(this.selected.quoteNo) + ' vs ' + this.dash(this.selected.oQuoteNo)
    },
    // The order Next and Back walk in: every row of the list the tab is showing,
    // after the filters, in the order the grid is displaying them - across pages,
    // not only the fifty on screen. The grid sorts client-side, so its sort has to
    // be reproduced here: this is PrimeVue 2.0.7's own sortSingle comparator applied
    // to the same gridRows the grid was handed, so the sentinel that keeps blanks at
    // the bottom is in play here too. What comes back out are the rows exactly as the
    // server sent them, because that is what the dialog reads.
    orderedRows() {
      const field = this.sortField
      if (!field) {
        return this.rows
      }
      const order = this.sortOrder
      const byId = {}
      this.rows.forEach(row => {
        byId[row.id] = row
      })
      const sorted = this.gridRows.slice().sort((a, b) => {
        const left = a[field]
        const right = b[field]
        let result
        if (left == null && right != null) {
          result = -1
        } else if (left != null && right == null) {
          result = 1
        } else if (left == null && right == null) {
          result = 0
        } else if (typeof left === 'string' && typeof right === 'string') {
          result = left.localeCompare(right, undefined, { numeric: true })
        } else {
          result = left < right ? -1 : left > right ? 1 : 0
        }
        return order * result
      })
      return sorted.map(row => byId[row.id] || row)
    },
    // Where the open pair sits in that order. -1 only if the selection is somehow
    // not in the list at all, which reads as "no walk" rather than as position zero.
    walkIndex() {
      if (!this.selected) {
        return -1
      }
      const id = this.selected.id
      return this.orderedRows.findIndex(row => row.id === id)
    },
    walkPosition() {
      const index = this.walkIndex
      if (index < 0) {
        return ''
      }
      return index + 1 + ' of ' + this.orderedRows.length
    },
    canWalkBack() {
      return this.walkIndex > 0
    },
    canWalkNext() {
      const index = this.walkIndex
      return index >= 0 && index < this.orderedRows.length - 1
    },
    proofItems() {
      if (!this.selected || !this.selected.proof) {
        return []
      }
      return String(this.selected.proof)
        .split('; ')
        .map(s => s.trim())
        .filter(s => s.length > 0)
    },
    compareFields() {
      const r = this.selected
      if (!r) {
        return []
      }
      const money = v => this.formatMoney(v)
      const text = v => this.dash(v)
      const date = v => this.formatDate(v)
      const same = (a, b) => this.sameValue(a, b)
      // A date and the person it belongs to, read as one line. Either half can be
      // missing - only when both are does the whole line become a dash.
      const dateBy = (value, by) => {
        const d = this.formatDate(value)
        if (d === DASH && !by) {
          return DASH
        }
        return by ? d + ' by ' + by : d
      }
      return [
        this.pair('Quote no', text(r.quoteNo), text(r.oQuoteNo), same(r.quoteNo, r.oQuoteNo)),
        this.pair('Description as typed', text(r.description), text(r.oDescription), same(r.description, r.oDescription)),
        this.pair('Customer', text(r.custName), text(r.oCustName), same(r.custName, r.oCustName)),
        this.pair('Site address', text(r.address), text(r.oAddress), same(r.address, r.oAddress)),
        this.pair('Postcode', text(r.postCode), text(r.oPostCode), same(r.postCode, r.oPostCode)),
        // The town belongs to the site the pair shares, so there is one value for both.
        this.pair('Town', text(r.town), text(r.town), true),
        this.pair('Project type', text(r.projType), text(r.oProjType), same(r.projType, r.oProjType)),
        this.pair('Product type', text(r.productType), text(r.oProductType), same(r.productType, r.oProductType)),
        this.pair('Quote £', money(r.productValue), money(r.oProductValue), same(r.productValue, r.oProductValue)),
        this.pair('Grade', text(r.quoteGrade), text(r.oQuoteGrade), same(r.quoteGrade, r.oQuoteGrade)),
        this.pair('Status', text(r.status), text(r.oStatus), same(r.status, r.oStatus)),
        this.pair(
          'Created',
          dateBy(r.dateCreated, r.createdBy),
          dateBy(r.oDateCreated, r.oCreatedBy),
          same(r.dateCreated, r.oDateCreated) && same(r.createdBy, r.oCreatedBy)
        ),
        this.pair(
          'Updated',
          dateBy(r.dateUpdated, r.updatedBy),
          dateBy(r.oDateUpdated, r.oUpdatedBy),
          same(r.dateUpdated, r.oDateUpdated) && same(r.updatedBy, r.oUpdatedBy)
        ),
        this.pair(
          'Status changed',
          date(r.dateStatusChanged),
          date(r.oDateStatusChanged),
          same(r.dateStatusChanged, r.oDateStatusChanged)
        ),
        this.pair('Received', date(r.dateReceived), date(r.oDateReceived), same(r.dateReceived, r.oDateReceived)),
        this.pair('Branch', text(r.branch), text(r.oBranch), same(r.branch, r.oBranch)),
        this.pair('Region', text(r.region), text(r.oRegion), same(r.region, r.oRegion)),
        this.pair('Sales exec', text(r.salesExec), text(r.oSalesExec), same(r.salesExec, r.oSalesExec)),
        this.pair('Contact', text(r.contact), text(r.oContact), same(r.contact, r.oContact)),
        this.pair('Lost reason', text(r.lostReason), text(r.oLostReason), same(r.lostReason, r.oLostReason))
      ]
    }
  },
  watch: {
    canView(val) {
      if (val) {
        this.loadCounts()
        this.loadRows()
      }
    },
    liveOnly() {
      // liveOnly changes what every tab contains, so the tab counts move with it.
      this.loadCounts()
      this.loadRows()
    },
    // So do the three dropdowns and the checkbox.
    productTypes() {
      this.onSharedFilterChange()
    },
    branches() {
      this.onSharedFilterChange()
    },
    projTypes() {
      this.onSharedFilterChange()
    },
    hideDiffProductType() {
      this.onSharedFilterChange()
    },
    confidence() {
      // Guarded so that moving to list 3, which resets this to All, does not fire a
      // second request on top of the one the tab change already made.
      if (this.listHasConfidence) {
        this.loadRows()
      }
    },
    search() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
      }
      this.searchTimer = setTimeout(() => {
        this.searchTimer = null
        this.loadRows()
      }, SEARCH_DEBOUNCE_MS)
    }
  },
  mounted() {
    window.addEventListener('keydown', this.onKeydown)
    this.loadFilterOptions()
    if (this.canView) {
      this.loadCounts()
      this.loadRows()
    }
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeydown)
    if (this.searchTimer) {
      clearTimeout(this.searchTimer)
    }
  },
  methods: {
    selectTab(list) {
      if (list === this.activeList) {
        return
      }
      this.activeList = list
      // The grid is re-created on a tab change (its :key carries the list), which
      // drops its own sort, so the page's copy of it goes too.
      this.sortField = null
      this.sortOrder = 1
      if (list === 3) {
        // Nothing to filter on, and leaving a stale value set would silently
        // narrow the list with no visible control saying so.
        this.confidence = 'All'
      }
      this.loadRows()
    },
    countFor(list) {
      const n = this.counts[String(list)]
      return n === undefined || n === null ? DASH : n
    },
    queryParams() {
      const params = { liveOnly: this.liveOnly }
      if (this.listHasConfidence && this.confidence && this.confidence !== 'All') {
        params.confidence = this.confidence
      }
      const search = (this.search || '').trim()
      if (search) {
        params.search = search
      }
      // The checkbox is not sent for list 3, where it has no control on screen.
      return this.addSharedFilters(params, this.listHasProductTypeCheckbox)
    },
    // The counts follow Live only and the new filters, the way they followed Live only
    // before. The checkbox goes whatever the tab: the server applies it to lists 1 and
    // 2 only, so the badges of tabs 1 and 2 stay right while tab 3 is showing.
    countParams() {
      return this.addSharedFilters({ liveOnly: this.liveOnly }, true)
    },
    addSharedFilters(params, withCheckbox) {
      if (this.productTypes && this.productTypes.length) {
        params.productTypes = this.productTypes.slice()
      }
      if (this.branches && this.branches.length) {
        params.branches = this.branches.slice()
      }
      if (this.projTypes && this.projTypes.length) {
        params.projTypes = this.projTypes.slice()
      }
      if (withCheckbox && this.hideDiffProductType) {
        params.hideDiffProductType = true
      }
      return params
    },
    onSharedFilterChange() {
      this.loadCounts()
      this.loadRows()
    },
    loadFilterOptions() {
      this.duplicateEnquiryService
        .getFilterOptions()
        .then(options => {
          this.filterOptions = options
        })
        .catch(() => {
          // The page still works without them: the dropdowns just offer nothing.
          this.filterOptions = { productTypes: [], branches: [], projTypes: [] }
        })
    },
    loadRows() {
      // Every reload replaces the rows, so the pair the dialog is showing may no
      // longer be in the list. Clearing it here closes the dialog, and covers the
      // tab change, both filters and the search, rather than only the paths that
      // remember to. The paginator goes back to the first page for the same reason:
      // the rows underneath it are not the rows it was showing.
      this.selected = null
      this.first = 0
      const seq = ++this.requestSeq
      const list = this.activeList
      this.loading = true
      this.errorMessage = null
      this.duplicateEnquiryService
        .getPairs(list, this.queryParams())
        .then(result => {
          if (seq !== this.requestSeq) {
            return
          }
          this.rows = (result.rows || []).map(this.decorate)
          this.recCount = result.recCount
          this.loading = false
        })
        .catch(() => {
          if (seq !== this.requestSeq) {
            return
          }
          this.rows = []
          this.recCount = 0
          this.selected = null
          this.loading = false
          this.errorMessage = 'The duplicate enquiries could not be loaded. Please try again.'
        })
    },
    loadCounts() {
      const seq = ++this.countsSeq
      this.duplicateEnquiryService
        .getCounts(this.countParams())
        .then(data => {
          if (seq === this.countsSeq) {
            this.counts = data || {}
          }
        })
        .catch(() => {
          if (seq === this.countsSeq) {
            this.counts = {}
          }
        })
    },
    // The grid sorts on the value it is given, so the two columns that display
    // something other than their raw value get a sortable companion field.
    decorate(row) {
      const copy = Object.assign({}, row)
      copy.confidenceRank = CONFIDENCE_RANK[row.confidence] || 9
      copy.createdSort = row.dateCreated ? new Date(String(row.dateCreated).slice(0, 10) + 'T00:00:00').getTime() : null
      return copy
    },
    // The header wraps inside the column rather than widening it, so a long label
    // like "Project live quotes added up £" costs three lines, not 230px.
    headerStyleFor(col) {
      return 'min-width: ' + col.width + '; white-space: normal; word-break: break-word;'
    },
    // Free text wraps; a quote number, an amount, a date or a count never does.
    bodyStyleFor(col) {
      const wrapping = col.wrap
        ? 'white-space: normal; word-break: break-word;'
        : 'white-space: nowrap;'
      return 'min-width: ' + col.width + '; ' + wrapping
    },
    // A max-width on a table cell is not reliably honoured, so the cap that keeps a
    // wrapping column at its stated width goes on the text inside the cell.
    cellStyleFor(col) {
      if (!col.wrap) {
        return null
      }
      return 'display: inline-block; max-width: ' + col.width + '; word-break: break-word;'
    },
    onRowClick(event) {
      const clicked = event && event.data ? event.data : null
      if (!clicked) {
        this.selected = null
        return
      }
      // gridRows may have handed the grid a patched copy of this row, so the
      // comparison dialog is given the row exactly as the server sent it.
      const original = this.rows.find(row => row.id === clicked.id)
      this.selected = original || clicked
    },
    closeComparison() {
      this.selected = null
    },
    // Next and Back only ever move `selected`. The Dialog stays mounted, so the pair
    // is replaced in place with no close-and-reopen flicker, and because the grid's
    // selection is the same `selected`, its highlight moves with the dialog.
    walkTo(step) {
      const index = this.walkIndex
      if (index < 0) {
        return
      }
      const target = index + step
      if (target < 0 || target >= this.orderedRows.length) {
        return
      }
      this.selected = this.orderedRows[target]
      // The walk runs over the whole list, not the page on screen, so the grid is
      // moved to the page the new row is on - otherwise closing the dialog leaves
      // the highlighted row somewhere the user cannot see.
      const size = this.rowsPerPage || 50
      this.first = Math.floor(target / size) * size
    },
    walkBack() {
      this.walkTo(-1)
    },
    walkNext() {
      this.walkTo(1)
    },
    // PrimeVue 2.0.7's Dialog binds a keydown listener only to trap Tab, so Escape
    // is the page's job for as long as this version is the one in package.json - and
    // the arrow keys that walk the list are the page's job for the same reason.
    onKeydown(event) {
      if (!this.selected) {
        return
      }
      if (event.key === 'Escape') {
        this.closeComparison()
      } else if (event.key === 'ArrowLeft') {
        this.walkBack()
      } else if (event.key === 'ArrowRight') {
        this.walkNext()
      }
    },
    openEnquiry(oppId) {
      if (!oppId) {
        return
      }
      // The detail route's param is named :custid but carries the enquiry (opp) id.
      // Always a new tab (Mohammed, 2026-09-28): the list and the comparison stay open here.
      window.open('/enquiries/' + oppId + '/detail', '_blank')
    },
    // `same` decides the tick and the amber "differs" highlight, so it is given
    // the raw comparison wherever the displayed text loses information - £12,345.40
    // and £12,345.60 both read "£12,345" but are not the same quote value.
    pair(label, left, right, rawSame) {
      const same = rawSame === undefined ? left === right : rawSame
      return { label: label, left: left, right: right, same: same }
    },
    // Nothing, empty and null all show as the same dash, so they count as equal;
    // anything else is compared as it came back from the server.
    sameValue(a, b) {
      const blank = v => v === null || v === undefined || v === ''
      if (blank(a) && blank(b)) {
        return true
      }
      return a === b
    },
    chipClass(confidence) {
      const suffix = { High: 'high', Medium: 'medium', Low: 'low' }[confidence] || 'none'
      return 'de-chip de-chip-' + suffix
    },
    cellText(row, col) {
      const raw = col.display ? row[col.display] : row[col.field]
      if (col.kind === 'money') {
        return this.formatMoney(raw)
      }
      if (col.kind === 'date') {
        return this.formatDate(raw)
      }
      if (col.kind === 'int') {
        return this.formatInt(raw)
      }
      return this.dash(raw)
    },
    dash(value) {
      if (value === null || value === undefined || value === '') {
        return DASH
      }
      return String(value)
    },
    // £#,##0 - whole pounds, no decimals. Deliberately not the generic report
    // grid's Number formatter, which shows pence.
    formatMoney(value) {
      if (value === null || value === undefined || value === '') {
        return DASH
      }
      const n = Number(value)
      // isFinite, not isNaN: the nulls-last sentinel of gridRows is a blank cell.
      if (!isFinite(n)) {
        return DASH
      }
      const rounded = Math.round(Math.abs(n))
      return (n < 0 ? '-£' : '£') + rounded.toLocaleString('en-GB')
    },
    formatInt(value) {
      if (value === null || value === undefined || value === '') {
        return DASH
      }
      const n = Number(value)
      return isFinite(n) ? n.toLocaleString('en-GB') : DASH
    },
    // "yyyy-MM-dd" -> "dd/MM/yyyy", split rather than parsed so no timezone can
    // move the day.
    formatDate(value) {
      if (value === null || value === undefined || value === '') {
        return DASH
      }
      const parts = String(value).slice(0, 10).split('-')
      if (parts.length !== 3) {
        return String(value)
      }
      return parts[2] + '/' + parts[1] + '/' + parts[0]
    }
  }
}
</script>

<style scoped>
/* The horizontal scroll and the per-cell wrapping are set by autoLayout and by the
   inline header/body styles the columns carry, so that they cannot be lost to a
   deep-selector or specificity fight with the theme. Only the cursor is left here. */
.de-grid /deep/ .p-datatable-tbody > tr {
  cursor: pointer;
}

.de-filter {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.de-filter-label {
  margin: 0;
  font-weight: 600;
}

.de-filter-hint {
  color: #6c757d;
}

.de-chip {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #ffffff;
  white-space: nowrap;
}

.de-chip-high {
  background-color: #2e7d32;
}

.de-chip-medium {
  background-color: #ef6c00;
}

.de-chip-low {
  background-color: #c1440e;
}

.de-chip-none {
  background-color: #9e9e9e;
}

/* The Dialog's header is flex with space-between and the close X follows this
   wrapper, so the wrapper takes the width left over and pushes the walk to the
   right, leaving it beside the X. */
.de-dialog-header {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
}

.de-walk {
  display: flex;
  align-items: center;
  gap: 2px;
  white-space: nowrap;
}

.de-walk-position {
  font-size: 0.85rem;
  font-weight: 600;
  color: #6c757d;
  min-width: 5.5rem;
  text-align: center;
}

.de-compare {
  width: 100%;
  border-collapse: collapse;
}

.de-compare th,
.de-compare td {
  border: 1px solid #dee2e6;
  padding: 5px 8px;
  text-align: left;
  vertical-align: top;
}

.de-compare th:first-child {
  width: 15%;
  font-weight: 600;
  background-color: #f8f9fa;
  white-space: nowrap;
}

.de-compare th:not(:first-child),
.de-compare td {
  width: 42.5%;
}

.de-compare thead th {
  background-color: #f8f9fa;
}

.de-differs {
  background-color: #fff4e5;
}

.de-same-tick {
  color: #2e7d32;
  font-size: 0.75rem;
  margin-left: 4px;
}
</style>
